import React, { memo, useCallback, useMemo, type Dispatch, type SetStateAction } from 'react'
import { useSelector } from 'react-redux'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import {
  ContractorStepDetailsCard,
  ContractorStepDetailsCardUsageVariant,
} from 'src/components/contractorStepDetailsCard/ContractorStepDetailsCard'
import { mapCollectedDataStatusToCardStatus } from 'src/components/contractorStepDetailsCard/utils'
import { ContractorStepDetailsCardStatus } from 'src/components/contractorStepDetailsCard/types'
import { CollectedDataStatus } from 'src/constants/collectedData'
import { getPartnerId } from 'src/selectors/user'
import { getContractorStepItemState } from '../contractorStepDetailsCard/hooks/useContractorStepItemState'
import { useStepContentBlocks } from '../contractorStepDetailsCard/hooks/useStepContentBlocks'
import { ContractorStepDetailsModal } from './ContractorStepDetailsModal'

export interface ContractorGroupedStepItemProps {
  collectedDataItems: CollectedData[]
  contractorDetails: ContractorDetails
  delayed?: boolean
  groupKey: string
  openModalId: string | null
  selectedModalItemId: string | null
  isRequestApprove?: boolean
  onRefreshContractors: () => void | Promise<void>
  setOpenModalId: Dispatch<SetStateAction<string | null>>
  setSelectedModalItemId: Dispatch<SetStateAction<string | null>>
  isSubcontractor?: boolean
}

const ContractorGroupedStepItemComponent = ({
  collectedDataItems,
  contractorDetails,
  delayed,
  groupKey,
  openModalId,
  selectedModalItemId,
  isRequestApprove,
  onRefreshContractors,
  setOpenModalId,
  setSelectedModalItemId,
  isSubcontractor,
}: ContractorGroupedStepItemProps) => {
  const partnerId = useSelector(getPartnerId)
  const modalId = `group-${groupKey}`
  const isStepDetailsModalOpen = openModalId === modalId

  const selectedCollectedData = useMemo(
    () =>
      selectedModalItemId
        ? collectedDataItems.find((item) => item.id === selectedModalItemId)
        : collectedDataItems[0],
    [collectedDataItems, selectedModalItemId],
  )

  const openModalForItem = useCallback(
    (itemId: string) => {
      setSelectedModalItemId(itemId)
      setOpenModalId(modalId)
    },
    [modalId, setOpenModalId, setSelectedModalItemId],
  )

  const closeModal = useCallback(() => {
    setOpenModalId(null)
    setSelectedModalItemId(null)
  }, [setOpenModalId, setSelectedModalItemId])

  const firstItem = collectedDataItems[0]
  const firstItemState = getContractorStepItemState({
    collectedData: firstItem,
    contractorDetails,
    isRequestApprove,
    isSubcontractor,
  })

  const isRecollecting = collectedDataItems.some((item) => item.isRecollecting)
  const hasAnyApproved = collectedDataItems.some(
    (item) => item.status === CollectedDataStatus.Completed,
  )
  const baseCardStatus = hasAnyApproved
    ? ContractorStepDetailsCardStatus.Approved
    : mapCollectedDataStatusToCardStatus(firstItemState.stepStatus)
  const cardStatus: ContractorStepDetailsCardStatus =
    firstItemState.expiresSoon && baseCardStatus === ContractorStepDetailsCardStatus.Approved
      ? ContractorStepDetailsCardStatus.ExpiringSoon
      : baseCardStatus

  const contentBlocks = useStepContentBlocks({
    collectedDataItems,
    contractorDetails,
    partnerId,
    isRequestApprove,
    isSubcontractor,
    onRefreshContractors,
    openModalForItem,
  })

  const showDetails = contentBlocks.some((b) => b.onReviewStepDataAction != null)
  const showRecollectButton = contentBlocks.some((b) => b.showRecollectButton)

  const recollectableBlock = contentBlocks.find((b) => b.showRecollectButton)
  let collectedDataForCard: CollectedData = firstItem

  if (recollectableBlock && recollectableBlock.collectedData) {
    collectedDataForCard = recollectableBlock.collectedData
  }

  const handleReviewStepData = useCallback(() => {
    const block = contentBlocks[0]

    if (block.collectedData) {
      openModalForItem(block.collectedData.id)
    }
  }, [contentBlocks, openModalForItem])

  const onReviewStepDataAction =
    showDetails && contentBlocks.length === 1 ? handleReviewStepData : undefined

  return (
    <>
      <ContractorStepDetailsCard
        variant={ContractorStepDetailsCardUsageVariant.Contractor}
        status={cardStatus}
        isRecollecting={isRecollecting}
        showDelayedBanner={delayed}
        onReviewStepDataAction={onReviewStepDataAction}
        collectedData={collectedDataForCard}
        contractorDetails={contractorDetails}
        onRefreshContractors={onRefreshContractors}
        showRecollectButton={showRecollectButton}
        contentBlocks={contentBlocks}
      />

      {isStepDetailsModalOpen && selectedCollectedData && (
        <ContractorStepDetailsModal
          collectedData={selectedCollectedData}
          contractorDetails={contractorDetails}
          delayed={delayed}
          isCombinedStep={collectedDataItems.length > 1}
          isRequestApprove={isRequestApprove}
          isSubcontractor={isSubcontractor}
          onRefreshContractors={onRefreshContractors}
          onClose={closeModal}
        />
      )}
    </>
  )
}

export const ContractorGroupedStepItem = memo(ContractorGroupedStepItemComponent)
